import { type IAppointmentCreditState } from '@principle-theorem/ng-principle-shared';

export interface IAppointmentCreditStateResult {
  readonly invoicePath: string;
  readonly creditState?: IAppointmentCreditState;
  readonly creditStateQueryFailed: boolean;
}

/**
 * The credit figures a checkout can show, both read from the same credit
 * emission so they can never disagree about what has already been spent.
 */
export interface IAppointmentCreditTotals {
  readonly matchingTotal: number;
  readonly unreservedTotal: number;
  /**
   * Every credit the patient still has, whoever it is reserved for. The box
   * leads with this so it agrees with the Account Credit payment option.
   */
  readonly availableTotal: number;
}

const noCreditTotals: IAppointmentCreditTotals = {
  matchingTotal: 0,
  unreservedTotal: 0,
  availableTotal: 0,
};

export class AppointmentCreditStateTotals {
  private readonly _lastKnownTotals = new Map<
    string,
    IAppointmentCreditTotals
  >();

  get(
    creditStateResult: IAppointmentCreditStateResult
  ): IAppointmentCreditTotals {
    const { invoicePath, creditState, creditStateQueryFailed } =
      creditStateResult;
    if (creditState) {
      const totals: IAppointmentCreditTotals = {
        matchingTotal: creditState.matchingTotal,
        unreservedTotal: creditState.unreservedTotal,
        availableTotal: creditState.availableTotal,
      };
      this._lastKnownTotals.set(invoicePath, totals);
      return totals;
    }
    if (creditStateQueryFailed) {
      return this._lastKnownTotals.get(invoicePath) ?? noCreditTotals;
    }
    this._lastKnownTotals.delete(invoicePath);
    return noCreditTotals;
  }
}
